import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Loader2,
  Sparkles,
  Upload,
  ArrowLeft,
  ArrowRight,
  Download,
  Share2,
  ShieldCheck,
  Lock,
  RotateCcw,
  Camera,
  Aperture,
  SwitchCamera,
} from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useTryOnCredits } from "@/context/TryOnCreditsContext";
import { SchoolVerifyModal } from "@/components/SchoolVerifyModal";
import { mpCustom } from "@/lib/metaPixel";

// Rotating status lines during generation — creates the "wow" of anticipation.
const STATUS_LINES = [
  { label: "Reading your look", hint: "Mapping your face + pose" },
  { label: "Studying the dress", hint: "Every stitch and shimmer" },
  { label: "Fitting the silhouette", hint: "Draping you into it" },
  { label: "Softening the lighting", hint: "Editorial studio glow" },
  { label: "Adding the final polish", hint: "Retouching for the runway" },
  { label: "Almost ready", hint: "This is going to be gorgeous" },
];

const TIPS = [
  "Front-facing selfie, whole body if you can",
  "Plain background beats a busy one",
  "Bright, even lighting — no harsh shadows",
  "Neutral colored clothes work best under the dress",
];

// A subtle sparkle field that floats behind the reveal image.
function SparkleField() {
  const dots = Array.from({ length: 22 });
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((_, i) => {
        const left = Math.random() * 100;
        const top = Math.random() * 100;
        const size = 2 + Math.random() * 4;
        const delay = Math.random() * 2;
        const dur = 3 + Math.random() * 3;
        return (
          <motion.span
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 0.8, 0], scale: [0, 1, 0] }}
            transition={{ duration: dur, delay, repeat: Infinity, ease: "easeInOut" }}
            style={{ left: `${left}%`, top: `${top}%`, width: size, height: size }}
            className="absolute rounded-full bg-champagne shadow-[0_0_8px_rgba(247,231,206,0.9)]"
          />
        );
      })}
    </div>
  );
}

function ProgressDots({ current, total = 4 }) {
  return (
    <div className="flex items-center justify-center gap-2 pt-1">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          data-testid={`wizard-progress-${i + 1}`}
          className={`h-1 rounded-full transition-all duration-500 ${
            i + 1 === current
              ? "w-8 bg-rosemetal"
              : i + 1 < current
                ? "w-4 bg-ink/60"
                : "w-4 bg-ink/15"
          }`}
        />
      ))}
    </div>
  );
}

export function TryOnWizard({ product, onBuyCredits, onClose }) {
  // Steps:
  //   1  Upload selfie (with tips)
  //   2  Verify school (only when needed)
  //   3  Paywall (only when needed)
  //   4  Confirm & consent
  //   5  Generating (dark takeover)
  //   6  Reveal (image reveal + share)
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [mediaId, setMediaId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [shareable, setShareable] = useState(true);
  const [result, setResult] = useState(null);
  const [showBefore, setShowBefore] = useState(false);
  const [credits, setCredits] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [statusIdx, setStatusIdx] = useState(0);
  const globalCredits = useTryOnCredits();
  const fileRef = useRef();
  const dragRef = useRef();

  const loadCredits = () =>
    api
      .tryOnCredits()
      .then(({ data }) => setCredits(data))
      .catch(() => {});

  useEffect(() => {
    loadCredits();
    api
      .tryOnTiers()
      .then(({ data }) => setTiers(data.tiers || []))
      .catch(() => {});
  }, []);

  // Rotate the status ticker during generation.
  useEffect(() => {
    if (step !== 5) return;
    setStatusIdx(0);
    const t = setInterval(
      () => setStatusIdx((i) => Math.min(i + 1, STATUS_LINES.length - 1)),
      3800,
    );
    return () => clearInterval(t);
  }, [step]);

  const uploadFile = async (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.upload(fd);
      setMediaId(data.id);
    } catch (err) {
      toast.error(errMsg(err));
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const onFile = (e) => uploadFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    dragRef.current?.classList.remove("border-rosemetal", "bg-blush/70");
    uploadFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    dragRef.current?.classList.add("border-rosemetal", "bg-blush/70");
  };
  const onDragLeave = () => {
    dragRef.current?.classList.remove("border-rosemetal", "bg-blush/70");
  };

  // ---- Live camera ---------------------------------------------------
  // Feature-detected — mode toggle is only rendered when getUserMedia is
  // available. Users can flip between the front / rear camera on mobile.
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [mode, setMode] = useState("upload"); // "upload" | "camera"
  const [facingMode, setFacingMode] = useState("user"); // "user" | "environment"
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");

  const stopCamera = () => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  };

  const startCamera = async (facing = facingMode) => {
    setCameraError("");
    setCameraReady(false);
    stopCamera();
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 1600 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCameraReady(true);
    } catch (err) {
      setCameraError(
        err?.name === "NotAllowedError"
          ? "Camera permission denied. Enable it in your browser settings and try again."
          : 'Couldn\'t start the camera. You can still upload a photo below.',
      );
      setCameraReady(false);
    }
  };

  // Auto-start when the user switches to camera mode; auto-stop on unmount
  // and whenever they leave the upload step.
  useEffect(() => {
    if (mode === "camera" && step === 1) startCamera(facingMode);
    else stopCamera();
    return stopCamera;
  }, [mode, step]);

  const flipCamera = () => {
    const next = facingMode === "user" ? "environment" : "user";
    setFacingMode(next);
    startCamera(next);
  };

  const capturePhoto = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    // Mirror the front camera capture so what the user saw is what gets saved.
    if (facingMode === "user") {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", 0.92),
    );
    if (!blob) {
      toast.error("Couldn't capture — please try again.");
      return;
    }
    stopCamera();
    setMode("upload"); // swap back so the preview UI takes over post-capture
    const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: "image/jpeg" });
    uploadFile(file);
  };

  const needsVerify = credits && !credits.is_vip && !credits.school_verified;
  const outOfCredits =
    credits &&
    !credits.is_vip &&
    credits.school_verified &&
    credits.free_left <= 0 &&
    credits.credits <= 0;

  // Which visual step-dot are we on? Verify + paywall collapse into "dot 2".
  const dot = step <= 1 ? 1 : step <= 4 ? 2 : step === 5 ? 3 : 4;

  const advanceFromUpload = () => {
    if (!mediaId) {
      toast("Add a selfie to continue");
      return;
    }
    if (needsVerify) setStep(2);
    else if (outOfCredits) setStep(3);
    else setStep(4);
  };

  const generate = async () => {
    setStep(5);
    // Meta Pixel — custom TryOnStarted (fires only after the credit/verify gates have passed)
    try {
      mpCustom("TryOnStarted", {
        content_ids: [product.id],
        content_name: product.name,
        content_category: product.category_slug || product.category,
      });
    } catch { /* noop */ }
    try {
      const { data } = await api.tryOn({
        product_id: product.id,
        selfie_media_id: mediaId,
        shareable,
        color: product.color || null,
      });
      setResult(`${process.env.REACT_APP_BACKEND_URL}${data.result_url}`);
      globalCredits.refresh();
      // Meta Pixel — custom TryOnCompleted (fires only after server returns a result)
      try {
        mpCustom("TryOnCompleted", {
          content_ids: [product.id],
          content_name: product.name,
          content_category: product.category_slug || product.category,
        });
      } catch { /* noop */ }
      // Small delay so the "Almost ready" line has time to breathe.
      setTimeout(() => setStep(6), 400);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 402) {
        toast.error(errMsg(e));
        loadCredits();
        setStep(3);
      } else if (s === 403) {
        toast.error(errMsg(e));
        loadCredits();
        setStep(2);
      } else {
        toast.error(errMsg(e));
        setStep(4);
      }
    }
  };

  const downloadResult = async () => {
    if (!result) return;
    try {
      const r = await fetch(result);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `heathers-${(product.name || "look").replace(/\s+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download. Long-press the image and Save instead.");
    }
  };

  const shareResult = async () => {
    if (navigator.share && result) {
      try {
        const r = await fetch(result);
        const blob = await r.blob();
        const file = new File([blob], "look.png", { type: blob.type });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `Me in the ${product.name}`,
            text: `Just tried on the ${product.name} at Heather's Boutique ✨`,
          });
          return;
        }
      } catch {
        /* fallthrough */
      }
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied — paste it wherever.");
    } catch {
      toast("Screenshot the look and share it 🤎");
    }
  };

  const productImg = product.images?.[0];

  // Root wrapper. On the reveal step we allow the modal to grow taller.
  const wide = step === 5 || step === 6;

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink/70 backdrop-blur-md flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="tryon-wizard"
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className={`relative bg-ivory rounded-sm w-full ${wide ? "max-w-3xl" : "max-w-xl"} max-h-[92vh] overflow-hidden flex flex-col`}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          {step > 1 && step < 5 ? (
            <button
              data-testid="wizard-back"
              onClick={() => setStep(step === 4 ? 1 : Math.max(1, step - 1))}
              className="inline-flex items-center gap-1.5 font-label text-[10px] tracking-widest text-ink/60 hover:text-ink transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> BACK
            </button>
          ) : (
            <span className="font-label text-[10px] tracking-[0.25em] text-rosemetal">
              VIRTUAL TRY-ON
            </span>
          )}
          <button
            onClick={onClose}
            data-testid="wizard-close"
            className="text-ink/70 hover:text-ink transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-2">
          <ProgressDots current={dot} total={4} />
        </div>

        <div className="overflow-y-auto flex-1 px-6 sm:px-10 py-6">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                data-testid="wizard-step-1"
              >
                <p className="font-label text-[10px] tracking-widest text-rosemetal">STEP 1 · UPLOAD</p>
                <h3 className="font-display text-4xl leading-tight mt-2 mb-2">
                  Add a selfie of you.
                </h3>
                <p className="font-body text-sm text-muted-foreground mb-6">
                  We'll gently place your face + body into the{" "}
                  <span className="text-ink italic">{product.name}</span>. Your photo stays private
                  — only your try-on result is saved.
                </p>

                {/* Mode toggle — hidden until we know the browser supports camera */}
                {typeof navigator !== "undefined" && navigator.mediaDevices?.getUserMedia && !preview && (
                  <div
                    data-testid="wizard-mode-toggle"
                    className="mb-4 inline-flex items-center gap-1 p-1 rounded-full bg-blush/60 border border-ink/10"
                  >
                    <button
                      data-testid="wizard-mode-upload"
                      onClick={() => setMode("upload")}
                      className={`px-4 py-1.5 rounded-full font-label text-[10px] tracking-widest transition-colors ${mode === "upload" ? "bg-ink text-ivory" : "text-ink/60 hover:text-ink"}`}
                    >
                      <Upload className="inline w-3 h-3 mr-1.5 -mt-0.5" />
                      UPLOAD
                    </button>
                    <button
                      data-testid="wizard-mode-camera"
                      onClick={() => setMode("camera")}
                      className={`px-4 py-1.5 rounded-full font-label text-[10px] tracking-widest transition-colors ${mode === "camera" ? "bg-ink text-ivory" : "text-ink/60 hover:text-ink"}`}
                    >
                      <Camera className="inline w-3 h-3 mr-1.5 -mt-0.5" />
                      LIVE CAMERA
                    </button>
                  </div>
                )}

                {mode === "camera" && !preview ? (
                  /* --- LIVE CAMERA MODE --- */
                  <div
                    data-testid="wizard-camera"
                    className="relative bg-ink rounded-sm aspect-[4/5] max-h-[420px] mx-auto flex items-center justify-center overflow-hidden"
                  >
                    <video
                      ref={videoRef}
                      playsInline
                      muted
                      autoPlay
                      data-testid="wizard-camera-video"
                      className={`w-full h-full object-cover ${facingMode === "user" ? "scale-x-[-1]" : ""}`}
                    />
                    {!cameraReady && !cameraError && (
                      <div className="absolute inset-0 bg-ink flex flex-col items-center justify-center text-ivory">
                        <Loader2 className="w-6 h-6 animate-spin mb-3" />
                        <p className="font-body text-xs">Starting camera…</p>
                        <p className="font-body text-[10px] text-ivory/60 mt-2 max-w-[220px] text-center">
                          Grant camera permission in the browser prompt to continue.
                        </p>
                      </div>
                    )}
                    {cameraError && (
                      <div className="absolute inset-0 bg-ink/95 flex flex-col items-center justify-center text-ivory p-6 text-center">
                        <Camera className="w-8 h-8 mb-3 opacity-70" />
                        <p className="font-body text-xs max-w-[240px]">{cameraError}</p>
                        <button
                          onClick={() => setMode("upload")}
                          className="mt-4 font-label text-[9px] tracking-widest underline underline-offset-4"
                        >
                          UPLOAD A PHOTO INSTEAD →
                        </button>
                      </div>
                    )}
                    {cameraReady && (
                      <>
                        {/* Face-aligned guide oval */}
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                          <div className="w-[52%] h-[64%] rounded-full border border-ivory/40 shadow-[inset_0_0_60px_rgba(0,0,0,0.35)]" />
                        </div>
                        <button
                          onClick={flipCamera}
                          data-testid="wizard-camera-flip"
                          aria-label="Flip camera"
                          className="absolute top-3 right-3 w-10 h-10 rounded-full glass-dark text-ivory flex items-center justify-center hover:bg-ink/80 transition-colors"
                        >
                          <SwitchCamera className="w-4 h-4" />
                        </button>
                        <button
                          onClick={capturePhoto}
                          data-testid="wizard-camera-capture"
                          aria-label="Capture photo"
                          className="absolute bottom-6 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-ivory hover:bg-cream text-ink flex items-center justify-center shadow-[0_0_0_4px_rgba(255,255,255,0.25)] transition-colors"
                        >
                          <Aperture className="w-7 h-7" />
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                <div
                  ref={dragRef}
                  onClick={() => fileRef.current?.click()}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  data-testid="wizard-drop-zone"
                  className="relative cursor-pointer group border-2 border-dashed border-ink/25 hover:border-rosemetal bg-blush/30 rounded-sm aspect-[4/5] max-h-[420px] mx-auto flex items-center justify-center overflow-hidden transition-colors"
                >
                  {preview ? (
                    <>
                      <img
                        src={preview}
                        alt="Your selfie"
                        className="w-full h-full object-cover"
                      />
                      {uploading && (
                        <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin text-ivory" />
                        </div>
                      )}
                      {!uploading && (
                        <span className="absolute bottom-3 left-3 glass-dark text-ivory font-label text-[9px] tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
                          <RotateCcw className="w-3 h-3" /> TAP TO REPLACE
                        </span>
                      )}
                    </>
                  ) : (
                    <div className="text-center px-6">
                      <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-ink text-ivory flex items-center justify-center group-hover:bg-rosemetal transition-colors">
                        <Upload className="w-6 h-6" />
                      </div>
                      <p className="font-display text-2xl leading-tight">
                        Drop your selfie
                      </p>
                      <p className="font-body text-xs text-muted-foreground mt-2">
                        or tap to choose from your phone
                      </p>
                    </div>
                  )}
                </div>
                )}
                <input
                  ref={fileRef}
                  data-testid="wizard-upload"
                  type="file"
                  accept="image/*"
                  onChange={onFile}
                  className="hidden"
                />

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {TIPS.map((t, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-rosemetal shrink-0" />
                      <span className="font-body text-xs text-ink/70">{t}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
                data-testid="wizard-step-verify"
              >
                <p className="font-label text-[10px] tracking-widest text-rosemetal">
                  STEP 2 · STUDENTS ONLY
                </p>
                <h3 className="font-display text-4xl leading-tight mt-2 mb-3">
                  Verify your school.
                  <br />
                  <span className="italic text-rosemetal">First try-on is on us.</span>
                </h3>
                <p className="font-body text-sm text-muted-foreground mb-6">
                  Heather's is a students-only boutique. Confirm your school email — no
                  passwords, no waiting — and we'll unlock your first free AI try-on right
                  after.
                </p>
                <div className="bg-blush/40 border border-rosemetal/30 rounded-sm p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-rosemetal shrink-0 mt-0.5" />
                    <div>
                      <p className="font-body text-sm text-ink font-medium mb-1">
                        Magic link · no password
                      </p>
                      <p className="font-body text-xs text-ink/60 leading-relaxed">
                        We'll email a one-tap sign-in link to your school address. Link
                        expires in 15 minutes.
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  data-testid="wizard-open-verify"
                  onClick={() => setVerifyOpen(true)}
                  className="w-full bg-ink text-ivory py-4 font-label text-[11px] tracking-[0.25em] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" /> VERIFY MY SCHOOL
                </button>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
                data-testid="wizard-step-paywall"
              >
                <p className="font-label text-[10px] tracking-widest text-rosemetal">
                  STEP 2 · PICK A PACK
                </p>
                <h3 className="font-display text-4xl leading-tight mt-2 mb-3">
                  Try on as many looks as you like.
                </h3>
                <p className="font-body text-sm text-muted-foreground mb-6">
                  Credits never expire. Use them across every dress in the boutique.
                </p>
                <div className="grid gap-3">
                  {tiers.map((t) => (
                    <button
                      key={t.pack}
                      data-testid={`wizard-buy-${t.pack}`}
                      onClick={() => onBuyCredits(t.pack)}
                      className={`relative w-full text-left border rounded-sm px-5 py-4 hover:shadow-lg hover:-translate-y-0.5 transition-all ${
                        t.highlight
                          ? "border-rosemetal bg-blush/30"
                          : "border-border hover:border-rosemetal"
                      }`}
                    >
                      {t.badge && (
                        <span className="absolute -top-2.5 right-4 bg-ink text-champagne font-label text-[8px] tracking-[0.25em] px-2 py-0.5 rounded-full">
                          {t.badge}
                        </span>
                      )}
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-display text-xl text-ink">{t.name}</span>
                        <span className="font-display text-2xl text-rosemetal">
                          ${t.price.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between font-label text-[9px] tracking-widest text-ink/60">
                        <span>
                          {t.credits} {t.credits === 1 ? "AI IMAGE" : "AI IMAGES"}
                        </span>
                        <span>
                          {t.credits === 1
                            ? "1 IMAGE"
                            : `~$${t.per_image.toFixed(2)} / IMAGE`}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
                <Link
                  to="/try-on/pricing"
                  data-testid="wizard-see-all-plans"
                  onClick={onClose}
                  className="mt-4 block text-center font-label text-[10px] tracking-widest text-ink/60 hover:text-rosemetal"
                >
                  SEE ALL PLANS + FAQ →
                </Link>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
                data-testid="wizard-step-confirm"
              >
                <p className="font-label text-[10px] tracking-widest text-rosemetal">
                  STEP 2 · CONFIRM
                </p>
                <h3 className="font-display text-4xl leading-tight mt-2 mb-1">
                  Ready to see it on you?
                </h3>
                <p className="font-body text-sm text-muted-foreground mb-6">
                  This one costs{" "}
                  <span className="text-ink">
                    {credits?.is_vip
                      ? "nothing — you're VIP"
                      : credits?.free_left > 0
                        ? "nothing — your first is free"
                        : "1 credit"}
                  </span>
                  .
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="border border-border rounded-sm overflow-hidden bg-blush">
                    <div className="aspect-[3/4]">
                      <img
                        src={preview}
                        alt="You"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="font-label text-[9px] tracking-widest text-ink/60 text-center py-2">
                      YOU
                    </p>
                  </div>
                  <div className="border border-border rounded-sm overflow-hidden bg-blush">
                    <div className="aspect-[3/4]">
                      <img
                        src={productImg}
                        alt={product.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <p className="font-label text-[9px] tracking-widest text-ink/60 text-center py-2">
                      THE DRESS
                    </p>
                  </div>
                </div>
                <label
                  className="flex items-start gap-2.5 cursor-pointer select-none border border-border rounded-sm p-4 bg-ivory hover:border-rosemetal transition-colors"
                >
                  <input
                    type="checkbox"
                    data-testid="wizard-consent"
                    checked={shareable}
                    onChange={(e) => setShareable(e.target.checked)}
                    className="mt-1 accent-rosemetal shrink-0"
                  />
                  <span className="font-body text-xs text-ink/80 leading-relaxed">
                    Let Heather feature my try-on in the boutique Lookbook (credit to me
                    &amp; my school). You can opt out anytime.
                  </span>
                </label>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="relative min-h-[420px] -mx-6 sm:-mx-10 -my-6 bg-ink text-ivory flex flex-col items-center justify-center px-6 py-10 overflow-hidden"
                data-testid="wizard-step-generating"
              >
                <SparkleField />
                <div className="relative z-10 text-center max-w-sm">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                    className="mx-auto mb-6 w-14 h-14 rounded-full border border-champagne/40 border-t-champagne"
                  />
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={statusIdx}
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -10, opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="font-display text-4xl leading-tight"
                    >
                      {STATUS_LINES[statusIdx].label}
                      <span className="text-champagne">…</span>
                    </motion.p>
                  </AnimatePresence>
                  <p className="font-body text-sm text-ivory/60 mt-3">
                    {STATUS_LINES[statusIdx].hint}
                  </p>
                  <div className="mt-8 h-0.5 w-full bg-ivory/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-champagne"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 24, ease: "easeInOut" }}
                    />
                  </div>
                  <p className="font-label text-[9px] tracking-widest text-ivory/40 mt-6">
                    HEATHER'S AI · ~20 SECONDS
                  </p>
                </div>
              </motion.div>
            )}

            {step === 6 && result && (
              <motion.div
                key="step6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="wizard-step-reveal"
              >
                <p className="font-label text-[10px] tracking-widest text-rosemetal">
                  DONE · YOU IN IT
                </p>
                <h3 className="font-display text-4xl leading-tight mt-2 mb-4">
                  This is you in the{" "}
                  <span className="italic text-rosemetal">{product.name}</span>.
                </h3>

                <div className="relative rounded-sm overflow-hidden bg-ink">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={showBefore ? "before" : "after"}
                      initial={{ opacity: 0, scale: 1.04 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      src={showBefore ? productImg : result}
                      alt={showBefore ? product.name : "Your try-on"}
                      className="relative w-full aspect-[3/4] object-cover object-top z-10"
                    />
                  </AnimatePresence>
                  {/* Sparkle drift OVER the image edges — subtle celebratory shimmer */}
                  <div className="absolute inset-0 z-20 pointer-events-none">
                    <SparkleField />
                  </div>
                  {/* Before/After toggle */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 glass-dark rounded-full p-1 inline-flex gap-1">
                    <button
                      data-testid="reveal-after"
                      onClick={() => setShowBefore(false)}
                      className={`font-label text-[9px] tracking-widest px-4 py-1.5 rounded-full transition-colors ${
                        !showBefore ? "bg-champagne text-ink" : "text-ivory"
                      }`}
                    >
                      YOU IN IT
                    </button>
                    <button
                      data-testid="reveal-before"
                      onClick={() => setShowBefore(true)}
                      className={`font-label text-[9px] tracking-widest px-4 py-1.5 rounded-full transition-colors ${
                        showBefore ? "bg-champagne text-ink" : "text-ivory"
                      }`}
                    >
                      THE DRESS
                    </button>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button
                    data-testid="reveal-download"
                    onClick={downloadResult}
                    className="border border-ink py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-ink hover:text-ivory transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Download className="w-3.5 h-3.5" /> DOWNLOAD
                  </button>
                  <button
                    data-testid="reveal-share"
                    onClick={shareResult}
                    className="border border-ink py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-ink hover:text-ivory transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-3.5 h-3.5" /> SHARE
                  </button>
                </div>
                <button
                  data-testid="reveal-lock"
                  onClick={onClose}
                  className="mt-3 w-full bg-ink text-ivory py-4 font-label text-[11px] tracking-[0.25em] rounded-sm hover:bg-rosemetal transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Lock className="w-4 h-4" /> LOVE IT — LOCK THIS DRESS
                </button>
                <button
                  data-testid="reveal-close"
                  onClick={onClose}
                  className="mt-2 w-full font-label text-[10px] tracking-widest text-ink/60 hover:text-ink py-2"
                >
                  CLOSE
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Sticky footer CTA (steps 1 and 4 only) */}
        {step === 1 && (
          <div className="px-6 sm:px-10 py-4 border-t border-border bg-ivory">
            <button
              data-testid="wizard-continue"
              onClick={advanceFromUpload}
              disabled={!mediaId || uploading}
              className="w-full bg-ink text-ivory py-4 font-label text-[11px] tracking-[0.25em] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:hover:bg-ink"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> UPLOADING
                </>
              ) : (
                <>
                  CONTINUE <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
        {step === 4 && (
          <div className="px-6 sm:px-10 py-4 border-t border-border bg-ivory">
            <button
              data-testid="wizard-generate"
              onClick={generate}
              className="w-full bg-ink text-ivory py-4 font-label text-[11px] tracking-[0.25em] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> STYLE ME NOW
            </button>
          </div>
        )}

        <SchoolVerifyModal
          open={verifyOpen}
          reason="tryon"
          onClose={() => {
            setVerifyOpen(false);
            // Re-fetch credits: if the user just verified in another tab,
            // we can move them straight into the confirm step.
            api
              .tryOnCredits()
              .then(({ data }) => {
                setCredits(data);
                if (data.school_verified) setStep(4);
              })
              .catch(() => {});
            globalCredits.refresh();
          }}
        />
      </motion.div>
    </div>
  );
}
